import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment as env } from 'environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SessionExpiredService } from '../services/session_expired/session_expired.service';
import { HotToastService } from '@ngneat/hot-toast';
import { UserService } from '../user/user.service';
import { LocalStorageService } from '../services/localstorage.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  exemptUrls: Array<{ state: string; url: string }> = [
    { state: 'user_shifts', url: '/user_shifts' },
    { state: 'conflicts', url: '/conflicts' },
    { state:'workflow-builder',url:'/workflows/' },
    { state: 'reports_asset_submission', url: '/reports/assets/asset_submission' },
    { state: undefined, url: '/telephony/agent'}
  ];
  exemptedServiceUrl: string[] = ['plugin_sync_history', 'installations', 'telephony', 'reports-service'];

  constructor(private router: Router,
              private sessionExpiredService: SessionExpiredService,
              private localStorageService: LocalStorageService,
              private userService: UserService,
              private toast: HotToastService,
              private translateService: TranslateService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const requestMessage = req?.method;
    const responseType = req?.responseType;
    let canSkipAuthError = false;
    if (this.canByPass(req.url)) {
      let headers: any = {
        'x-zuper-client': 'WEB_APP',
        'x-zuper-client-version': '3.0'
      };

      // data-import v3 dev
      if(req.url.includes('v3/imports'))
      {
        headers = {
          ...headers,
          'api-version': '3',
        }
      } 

      headers['authorization'] = `Bearer ${localStorage.getItem('auth_token')}`;
      req = req.clone({
        url: `${req.url}`,
        setHeaders: headers,
      });
    }
    if (req.headers.get('x-zuper-skip-error-handling')) {
      canSkipAuthError = true;
      req = req.clone({
        headers: req.headers.delete('x-zuper-skip-error-handling')
      });
    }
    if((localStorage.getItem('auth_token') && localStorage.getItem('auth_token').length) && !this.localStorageService.getLocalStorage('switching_account')){
      return next.handle(req).pipe(catchError((error, caught) => {
        //intercept the respons error and displace it to the console
        if (!canSkipAuthError && error instanceof HttpErrorResponse) {
          this.handleAuthError(error,requestMessage,responseType);
        }
        return throwError(() => error);
      }) as any
      );
    }else{
      return next.handle(req);
    }
  }

  canByPass(url: string): boolean {
    return url.includes('.zuperpro.com') || (env.environment === 'LOCAL' && url.includes('localhost'));
  }

  displayErrorMessage(err: any,requestMessage): void{
    if (requestMessage === 'GET') {
      if(err?.status === 404 && err?.url.includes('/notes/')){
        this.toast.error(this.translateService.instant('text.note_deleted'));
        return;
      }
      this.toast.error(
        this.getErrorHandlerToastMessage(
          err,
          'error_occurred.while_loading_data',
          'Error occurred while loading data'
        )
      );
    } else {
      this.toast.error(
        this.getErrorHandlerToastMessage(
          err,
          'text.unable_to_complete_the_requested_action',
          'Unable to complete the requested action'
        )
      );
    }
  }

  getErrorHandlerToastMessage(error, displayKey = 'text.unable_to_complete_the_requested_action', displayName = 'Unable to complete the requested action'): any {
    return (error?.data?.message ?? error?.error?.message ?? error?.message ?? this.translateService.instant(displayKey));
  }

  exemptedServices(url: string): boolean {
    let isExempted: boolean = false;
    this.exemptedServiceUrl.forEach((ele: any) => {
      if (url?.includes(ele)) {
        isExempted = true;
        return;
      }
    });
    return isExempted;
  }

    /**
   * manage errors
   * @param err
   * @returns {any}
   */
     private handleAuthError(err: HttpErrorResponse,requestMessage,responseType): Observable<any> {
      const windowState = window['urlState'];

      //handle your auth error or rethrow
      if (err.status === 401) {
        //navigate /delete cookies or whatever
        if (!this.exemptedServices(err.url)) {
          this.userService.isUserLoggedOut = true;
          this.localStorageService.removeAllLocalstorage();
          this.router.navigateByUrl('/login');
          this.sessionExpiredService.isSessionExpired.next(true);
          // if you've caught / handled the error, you don't want to rethrow it unless you also want downstream consumers to have to handle it as well.
          return of(err.message);
        }
      }else if ([0,504].includes(err.status)) {
        this.toast.error(
          this.translateService.instant('api_504_error_message')
        );
      } else{
        const isExempted = this.exemptUrls.filter((data: any)=> data?.state === windowState && err?.url.includes(data?.url)).length === 0;
        if(isExempted){
          if (responseType === 'blob') {
            const blob = err?.error?.text();
            blob?.then((response: any)=>{
              const error = JSON.parse(response);
              this.displayErrorMessage(error,requestMessage);
            });
          }else{
            this.displayErrorMessage(err,requestMessage);
          }
        }
      }
      throw err;
    }
}
